/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountsApiService } from '../../../shared/http';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ConfirmationModel, ErrorResponseModel, UserSessionModel } from '../../../../../core/models';
import { LogService, ModalService, RbacService, UserService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { TwoFactorAuthApiService } from '../../../../../core/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-two-factor-auth-page',
    styleUrls: ['./accounts-two-factor-auth.component.scss'],
    templateUrl: './accounts-two-factor-auth.component.html'
})

export class AccountsTwoFactorAuthComponent extends BaseComponent {
    /**
     * Has tier
     */
    public hasTier: boolean = false;

    /**
     * Page form group.
     */
    public twoFactorAuthForm: FormGroup;

    /**
     * Store the step state.
     */
    public formStep: number = 1;

    /**
     * State of the users 2FA
     */
    public twoFAEnabled: boolean = false;

    /**
     * State of the users 2FA
     */
    public twoFAType: 'SMS' | 'APP' | null = null;

    /**
     * Stored user object from user service.
     */
    public userObject: UserSessionModel = null;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private accountsApiService: AccountsApiService,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private modalService: ModalService,
        private rbacService: RbacService,
        private twoFactorAuthApiService: TwoFactorAuthApiService,
        private userService: UserService
    ) {
        super();

        this.hasTier = this.rbacService.getPageRequiredRoute('/api/v1/accounts/:id/2fa/setup')?.tier;
        this.setupFormGroup();
        this.registerButtons();

        this.userObject = this.userService.getUser();
        this.isOtpUser();
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.twoFactorAuthForm.get(group) as FormGroup;
    }

    /**
     * Get hashed phone number.
     */
    public getHashedNumber(): string {
        const formInput = this.twoFactorAuthForm.get('step1.registeredNumber').value
            ? 'step1.registeredNumber' : 'step2.phoneNumber';
        const phoneNumber = this.twoFactorAuthForm.get(`${formInput}`).value;
        if (phoneNumber) {
            return '*********' + phoneNumber.toString().slice(-2);
        }
    }

    /**
     * Cancel the setup process.
     */
    public cancelSetup(): void {
        this.formStep = 1;
        this.resetPage();
    }

    /**
     * Proceed to next step.
     */
    public nextStep(type?: 'SMS' | 'APP' | null): void {
        this.formStep++;

        if (type) {
            this.twoFAType = type;

            const state: 'disable' | 'enable' = type === 'APP' ? 'disable' : 'enable';
            this.twoFactorAuthForm.get('step2.countryCode')[state]();
            this.twoFactorAuthForm.get('step2.phoneNumber')[state]();
        }
    }

    /**
     * Submit auth code.
     */
    public setAuthCode($event: any): void {
        this.twoFactorAuthForm.get('step3.authCode').setValue($event);
        if (this.twoFactorAuthForm.get('step3').valid) {
            this.formStep = 1;
            this.twoFactorAuthForm.get('step1.registeredNumber').setValue(this.twoFactorAuthForm.get('step2.phoneNumber').value);
            this.twoFactorAuthForm.get('step2.phoneNumber').setValue(null);
            this.twoFAEnabled = true;
            this.userObject.otpEnabled = true;
            this.userObject.tfaType = this.twoFAType;
            this.userService.user = this.userObject;
        }
    }

    /**
     * Make API call to set up 2FA, on success go to next step.
     */
    public setupTwoFactorAuthSms(): void {
        const { countryCode, phoneNumber } = this.twoFactorAuthForm.get('step2').value;
        const phoneNumberString = phoneNumber.toString();
        this.twoFactorAuthApiService.setUp(this.userObject.id, {
            countryCode: countryCode,
            phoneNumber: phoneNumberString
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.formStep = 3;
                },
                error: (error: ErrorResponseModel): void => this.logService.error(
                    'AccountsTwoFactorAuthComponent:setupTwoFactorAuth',
                    'Error setting up Two Factor Auth',
                    error)
            });
    }

    /**
     * Disable number from 2FA
     */
    public disableNumberConfirmation(): void {
        const confirmationObject: ConfirmationModel = {
            title: 'accounts_two_factor_auth_sms',
            message: 'accounts_two_factor_auth_disable_sms_message',
            callBack: this.disable2FAApiCall.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Disable number from 2FA
     */
    public disableAppConfirmation(): void {
        const confirmationObject = {
            title: 'accounts_two_factor_auth_app',
            message: 'accounts_two_factor_auth_disable_app_message',
            callBack: this.disable2FAApiCall.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'two-factor-auth-next-step': [
                { path: '/api/v1/accounts/:id/2fa/setup', methods: ['POST'] }
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Disable 2FA API call
     * @private
     */
    private disable2FAApiCall(): Observable<any> {
        return new Observable((observer) => {
            this.twoFactorAuthApiService.disable(this.userObject.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.resetPage();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Reset page.
     * @private
     */
    private resetPage(): void {
        this.twoFactorAuthForm.get('step1.registeredNumber').setValue(null);
        this.userObject.otpEnabled = false;
        this.userObject.tfaType = null;
        this.userService.user = this.userObject;
        this.twoFAEnabled = false;
        this.twoFAType = null;
    }

    /**
     * Setup 2FA form.
     * @private
     */
    private setupFormGroup(): void {
        this.twoFactorAuthForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                registeredNumber: [null],
                appEnabled: [null]
            }),
            step2: this.formBuilder.group({
                countryCode: [null, Validators.required],
                phoneNumber: [null, [Validators.required, Validators.minLength(5)]]
            }),
            step3: this.formBuilder.group({
                authCode: [null, Validators.required]
            })
        });
    }

    /**
     * Check if user has OTP enabled.
     * @private
     */
    private isOtpUser(): void {
        const user: UserSessionModel = this.userObject;
        if ((user?.tfaType === 'SMS' || user?.tfaType === 'APP') && user?.otpEnabled) {
            this.twoFAType = user.tfaType;
            this.twoFAEnabled = true;
            if (user.tfaType === 'SMS') {
                this.getPhoneNumber(user);
            }
        }
    }

    /**
     * Get user phone number from API.
     * @param user
     * @private
     */
    private getPhoneNumber(user: any): void {
        this.accountsApiService.get(user.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data.phoneNumber) {
                        user.phoneNumber = data.phoneNumber;
                        this.patchFormData(user);
                    }
                }
            });
    }

    /**
     * Patch the data to the form.
     * @param user
     * @private
     */
    private patchFormData(user: { id?: { toString: () => string; }; phoneNumber: any; }): void {
        this.twoFactorAuthForm.get('step1.registeredNumber').setValue(user.phoneNumber);
    }
}

